import React from 'react';

/**
 * AssessmentStep2EnergyBaseline
 *
 * Props:
 * - energyBaseline: object containing energy and production baseline fields
 * - setEnergyBaseline: setter function to update energyBaseline state
 */
const AssessmentStep2EnergyBaseline = ({ energyBaseline, setEnergyBaseline }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Energy Baseline Information
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Provide your facility&apos;s current energy usage data to enable accurate savings
          calculations and ROI analysis.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Annual Electricity Usage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Annual Electricity Usage (kWh){' '}
            <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <input
            type="number"
            value={energyBaseline.annualKwh}
            onChange={(e) =>
              setEnergyBaseline({ ...energyBaseline, annualKwh: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            placeholder="e.g., 500000"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Check your annual utility bills or 12-month average
          </p>
        </div>

        {/* Annual Electricity Cost */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Annual Electricity Cost ($){' '}
            <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <input
            type="number"
            value={energyBaseline.annualElectricityCost}
            onChange={(e) =>
              setEnergyBaseline({
                ...energyBaseline,
                annualElectricityCost: e.target.value
              })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            placeholder="e.g., 75000"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Total annual electricity expenses including all charges
          </p>
        </div>

        {/* Peak Demand */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Peak Demand (kW)
          </label>
          <input
            type="number"
            value={energyBaseline.peakDemandKw}
            onChange={(e) =>
              setEnergyBaseline({ ...energyBaseline, peakDemandKw: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            placeholder="e.g., 150"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Maximum demand from utility bills (if available)
          </p>
        </div>

        {/* Average Monthly Bill */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Average Monthly Utility Bill ($){' '}
            <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <input
            type="number"
            value={energyBaseline.averageMonthlyBill}
            onChange={(e) =>
              setEnergyBaseline({
                ...energyBaseline,
                averageMonthlyBill: e.target.value
              })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            placeholder="e.g., 6250"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Average monthly electricity bill amount
          </p>
        </div>

        {/* Annual Gas Usage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Annual Gas Usage
          </label>
          <div className="flex space-x-2">
            <input
              type="number"
              value={energyBaseline.annualGasUsage}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  annualGasUsage: e.target.value
                })
              }
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="e.g., 10000"
            />
            <select
              value={energyBaseline.gasUsageUnit}
              onChange={(e) =>
                setEnergyBaseline({ ...energyBaseline, gasUsageUnit: e.target.value })
              }
              className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            >
              <option value="therms">Therms</option>
              <option value="cubic_feet">Cubic Feet</option>
            </select>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Natural gas consumption if applicable
          </p>
        </div>

        {/* Facility Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Facility Size (sq ft)
          </label>
          <input
            type="number"
            value={energyBaseline.facilitySize}
            onChange={(e) =>
              setEnergyBaseline({ ...energyBaseline, facilitySize: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            placeholder="e.g., 50000"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Total conditioned floor area
          </p>
        </div>

        {/* Operating Hours */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Operating Hours per Week
          </label>
          <input
            type="number"
            value={energyBaseline.operatingHours}
            onChange={(e) =>
              setEnergyBaseline({ ...energyBaseline, operatingHours: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            placeholder="e.g., 50"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Typical operating hours per week
          </p>
        </div>

        {/* Utility Rate Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Utility Rate Structure
          </label>
          <select
            value={energyBaseline.utilityRateType}
            onChange={(e) =>
              setEnergyBaseline({
                ...energyBaseline,
                utilityRateType: e.target.value
              })
            }
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
          >
            <option value="">Select rate type</option>
            <option value="flat_rate">Flat Rate</option>
            <option value="time_of_use">Time-of-Use (TOU)</option>
            <option value="demand_charges">Includes Demand Charges</option>
            <option value="tiered_rate">Tiered Rate Structure</option>
            <option value="unknown">Unknown/Unsure</option>
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Type of electricity rate structure
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Energy Monitoring */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Do you have energy monitoring systems?
          </label>
          <div className="space-y-2">
            {['Yes', 'No', 'Partial'].map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="hasEnergyMonitoring"
                  value={option.toLowerCase()}
                  checked={energyBaseline.hasEnergyMonitoring === option.toLowerCase()}
                  onChange={(e) =>
                    setEnergyBaseline({
                      ...energyBaseline,
                      hasEnergyMonitoring: e.target.value
                    })
                  }
                  className="mr-3 text-emerald-700 dark:text-emerald-500"
                />
                <span className="text-gray-700 dark:text-gray-300">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Historical Data */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Do you have 12+ months of energy data?
          </label>
          <div className="space-y-2">
            {['Yes', 'No', 'Partial'].map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="hasHistoricalData"
                  value={option.toLowerCase()}
                  checked={energyBaseline.hasHistoricalData === option.toLowerCase()}
                  onChange={(e) =>
                    setEnergyBaseline({
                      ...energyBaseline,
                      hasHistoricalData: e.target.value
                    })
                  }
                  className="mr-3 text-emerald-700 dark:text-emerald-500"
                />
                <span className="text-gray-700 dark:text-gray-300">{option}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mt-6">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
              Energy + Production Data
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Energy and production information enables us to calculate true energy productivity
              metrics, benchmark your facility against industry production standards, and provide
              realistic ROI estimates. All data is kept confidential.
            </p>
          </div>
        </div>
      </div>

      {/* Production Metrics Section */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Production Information
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Help us calculate your true energy productivity by providing production data. This
          enables accurate benchmarking and savings calculations.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Annual Production Volume */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Annual Production Volume{' '}
              <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              type="number"
              value={energyBaseline.annualProduction}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  annualProduction: e.target.value
                })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="e.g., 50000"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Total units produced annually
            </p>
          </div>

          {/* Production Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Production Unit <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <select
              value={energyBaseline.productionUnit}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  productionUnit: e.target.value
                })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            >
              <option value="">Select unit</option>
              <option value="tons">Tons</option>
              <option value="cases">Cases</option>
              <option value="units">Units</option>
              <option value="pounds">Pounds</option>
              <option value="gallons">Gallons</option>
              <option value="liters">Liters</option>
              <option value="pieces">Pieces</option>
              <option value="batches">Batches</option>
              <option value="other">Other</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Unit of measurement for production
            </p>
          </div>

          {/* Production Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Production Capacity (% of maximum)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={energyBaseline.productionCapacity}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  productionCapacity: e.target.value
                })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="e.g., 75"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Average capacity utilization (0-100%)
            </p>
          </div>

          {/* Average Production Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Average Production Rate (per hour)
            </label>
            <input
              type="number"
              value={energyBaseline.averageProductionRate}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  averageProductionRate: e.target.value
                })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="e.g., 500"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Units produced per operating hour
            </p>
          </div>

          {/* Annual Revenue */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Annual Revenue (optional)
            </label>
            <input
              type="number"
              value={energyBaseline.annualRevenue}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  annualRevenue: e.target.value
                })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              placeholder="e.g., 10000000"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Annual revenue from production ($)
            </p>
          </div>

          {/* Production Patterns */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Production Pattern
            </label>
            <select
              value={energyBaseline.productionSeasons}
              onChange={(e) =>
                setEnergyBaseline({
                  ...energyBaseline,
                  productionSeasons: e.target.value
                })
              }
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            >
              <option value="">Select pattern</option>
              <option value="continuous">Continuous (24/7 operation)</option>
              <option value="regular">Regular business hours</option>
              <option value="seasonal">Seasonal production</option>
              <option value="batch">Batch production</option>
              <option value="variable">Variable/custom schedule</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Production schedule pattern
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentStep2EnergyBaseline;
